import Image from "next/image";
import { useRouter } from "next/router";
import React, { useState } from "react";

import ImageTemplate from "@/components/editors/template-blocks/image/ImageTemplate";
import SliderTemplate from "@/components/editors/template-blocks/slider/SliderTemplate";
import VideoTemplate from "@/components/editors/template-blocks/video/VideoTemplate";
import { MainBlockModal } from "@/components/molecules/modals/MainBlockModal";
import {
  ConnectedService,
  EditorModalProps,
  TemplateOptionProp,
} from "@/hooks/useTemplatePartsEditor";
import { makeTemplatePart } from "@/states/page-editor/factory";
import {
  ImageBlockType,
  ImageTypeTemplate,
} from "@/states/page-editor/template-parts/image";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { getUniqueStr } from "@/utils/common";
import { NEXT_URL } from "@/utils/const";

import IconImageAdd from "public/icon/image-add.svg";

export interface MainBlockProps {
  part?: TemplatePart;
  onChange: (
    updated: TemplatePart | undefined,
    key: string,
    options?: TemplateOptionProp,
  ) => void;
  connectedService: ConnectedService | undefined;
  setEditorModalPrpps: (props: EditorModalProps) => void;
}

export interface TemplateThumbProps {
  label: string;
  value: ImageBlockType;
  thumb: string;
  select?: boolean;
}

/**
 * メインブロック
 * @returns
 */
const MainBlock: React.FC<MainBlockProps> = ({
  part,
  onChange,
  connectedService,
  setEditorModalPrpps,
}) => {
  const router = useRouter();
  const isIframe = router.asPath === NEXT_URL.IFRAME;

  // テンプレート選択モーダルの表示・非表示
  const [showListModal, setShowListModal] = useState<boolean>(false);

  // テンプレート選択リストにセットする値
  const blockTypes: TemplateThumbProps[] = [
    {
      label: "画像",
      value: "image",
      thumb: "/user/img/edit/page-create/image/image.jpg",
    },
    {
      label: "中央寄せ＋ボタン",
      value: "image.button",
      thumb: "/user/img/edit/page-create/image/image.button.jpg",
    },
  ];

  return (
    <>
      {!part ? (
        !isIframe ? (
          <>
            <div className="py-20 text-center">
              <button
                type="button"
                className="p-2 w-max h-min leading-[0px] text-white bg-theme-medium rounded-full hover:opacity-70 md:transition"
                onClick={() => setShowListModal(true)}
              >
                <IconImageAdd
                  width={24}
                  height={24}
                  className="fill-white"
                  title="メイン画像の設定"
                />
              </button>
            </div>
            {/* ブロックタイプ選択モーダル */}
            <MainBlockModal
              isOpen={showListModal}
              close={() => {
                setShowListModal(false);
              }}
            >
              <div className="p-6">
                メイン画像内に配置するブロックを選択してください。
                <ul className="grid grid-cols-1 gap-x-4 gap-y-6 mt-4 sm:grid-cols-2 md:grid-cols-3">
                  {blockTypes.map((item, index) => {
                    return (
                      <li
                        key={`template-${index}-${index}-${item.value}`}
                        className="text-center"
                      >
                        <button
                          className="block relative p-5 mb-4 w-full rounded-sm border border-primary cursor-pointer md:before:hidden md:hover:before:block md:before:absolute md:before:top-0 md:before:left-0 md:before:z-10 md:p-0 md:mb-2 md:before:w-full md:before:h-full md:before:content-[''] md:rounded md:before:border-4 md:before:border-primary md:before:pointer-events-none"
                          onClick={() => {
                            const blockKey = getUniqueStr();
                            onChange(
                              makeTemplatePart(
                                blockKey,
                                item.value,
                              ) as ImageTypeTemplate,
                              blockKey,
                            );
                            setShowListModal(false);
                          }}
                          type="button"
                        >
                          <Image
                            src={item.thumb}
                            alt=""
                            width={257}
                            height={228}
                            layout="responsive"
                            priority={true}
                            className="block"
                          />
                        </button>
                        <span>{item.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </MainBlockModal>
          </>
        ) : (
          <></>
        )
      ) : (
        /* eslint-disable-next-line tailwindcss/no-custom-classname */
        <div className="flex content-center place-content-center w-full text-center DraftEditor-editorContainer">
          <div className="w-full h-min md:w-[920px]">
            {part.type === "slider" ? (
              <>
                {!part.deleted && (
                  <div className="flex flex-col justify-center content-center h-min min-h-[100vw] md:mt-[40px] md:min-h-[0px]">
                    <SliderTemplate
                      part={part}
                      handleChange={(
                        updated: TemplatePart,
                        options?: TemplateOptionProp,
                      ) => {
                        onChange(updated, part.id, options);
                      }}
                      handleDelete={() => {
                        onChange(
                          {
                            ...part,
                            deleted: true,
                          },
                          part.id,
                        );
                      }}
                      setEditorModalPrpps={setEditorModalPrpps}
                    />
                  </div>
                )}
              </>
            ) : part.type === "video" ? (
              <>
                {!part.deleted && (
                  <div className="flex flex-col justify-center content-center mr-[26px] ml-[20px] min-h-[100vw] md:mx-0 md:min-h-[0px]">
                    <div className="-mt-5 h-min md:mt-[36px]">
                      <VideoTemplate
                        part={part}
                        handleChange={(
                          updated: TemplatePart,
                          options?: TemplateOptionProp,
                        ) => {
                          onChange(updated, part.id, options);
                        }}
                        handleDelete={() => {
                          onChange(
                            {
                              ...part,
                              deleted: true,
                            },
                            part.id,
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="mb-[40px]">
                <ImageTemplate
                  part={part as ImageTypeTemplate}
                  handleChange={(
                    updated: ImageTypeTemplate,
                    options?: TemplateOptionProp,
                  ) => {
                    onChange(updated, part.id, options);
                  }}
                  handleDelete={() => {
                    // 連携サービスが登録中だった場合、テンプレパーツ更新オプションをセット
                    const options =
                      part.id === connectedService?.templateBlockKey
                        ? { service: connectedService }
                        : undefined;
                    onChange(undefined, part.id, options);
                  }}
                  connectedService={connectedService}
                  setEditorModalPrpps={setEditorModalPrpps}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MainBlock;
